import { Add, Delete } from "@mui/icons-material";
import { Alert, Box, Button, FormGroup, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { createFeedback, FeedbackClassification } from "src/apis/feedback";
import { Collaborator } from "src/apis/company";
import CompanySelect from "src/pages/components/CompanySelect";
import CollaboratorSelect from "src/pages/components/CollaboratorSelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserPsychologist } from "src/utils/auth";
import { Id } from "src/utils/types";

const classificationOptions: FeedbackClassification[] = [
  "Observação geral",
  "Situação de risco",
  "Sugestão de ação",
];

const FeedbackPage: FC = () => {
  const [companyId, setCompanyId] = useState<Id>('');
  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [classification, setClassification] = useState<FeedbackClassification>("Observação geral");
  const [message, setMessage] = useState<string>("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isSubmitDisabled = useMemo(() => {
    return (
      !companyId ||
      message.trim().length === 0 ||
      loading
    );
  }, [companyId, message, loading]);

  const resetForm = () => {
    setCompanyId('');
    setCollaborator(null);
    setClassification("Observação geral");
    setMessage('');
    setAttachments([]);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    setLoading(true);
    try {
      await createFeedback({
        company_id: companyId,
        classification,
        message,
        collaborator_id: collaborator?.id_colaborador,
        attachments: attachments
          .filter((value) => value.trim().length > 0)
          .map((value) => ({ type: "link", value })),
      });
      setSuccess(true);
      setError('');
      resetForm();
    } catch (err: any) {
      setError(err?.message || "Erro ao enviar feedback");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer title="Feedback interno" error={error} guard={isUserPsychologist}>
      <Alert severity="info">
        Utilize este espaço para compartilhar observações relevantes com o time administrativo. Apenas a EezyCare visualizará essas informações.
      </Alert>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <CompanySelect
          companyId={companyId}
          setCompanyId={(value: Id) => {
            setCompanyId(value);
            setCollaborator(null);
          }}
          setError={setError}
        />

        {companyId && (
          <CollaboratorSelect
            companyId={companyId}
            collaboratorId={collaborator?.id_colaborador}
            setCollaborator={setCollaborator}
            setError={setError}
            isOptional
          />
        )}

        <FormGroup>
          <FormLabel>Classificação</FormLabel>
          <Select
            value={classification}
            onChange={(e) => setClassification(e.target.value as FeedbackClassification)}
          >
            {classificationOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <FormLabel>Mensagem</FormLabel>
          <TextField
            multiline
            minRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Descreva o contexto observado, riscos e recomendações."
          />
        </FormGroup>

        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <FormLabel>Anexos/links complementares</FormLabel>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={() => setAttachments((prev) => [...prev, ""])}
            >
              Adicionar link
            </Button>
          </Box>
          {attachments.length === 0 && (
            <TextField
              placeholder="Cole aqui um link de referência (opcional)"
              value={''}
              onChange={(e) => setAttachments([e.target.value])}
            />
          )}
          {attachments.map((value, index) => (
            <Box key={`attachment-${index}`} display="flex" gap={1} alignItems="center">
              <TextField
                fullWidth
                value={value}
                onChange={(e) =>
                  setAttachments((prev) => prev.map((item, idx) => (idx === index ? e.target.value : item)))
                }
                placeholder="https://..."
              />
              <Button
                variant="outlined"
                color="error"
                onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== index))}
              >
                <Delete />
              </Button>
            </Box>
          ))}
        </Box>

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetForm}
        />
      </form>
    </PageContainer>
  );
};

export default FeedbackPage;
